/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR = new Uint8Array([
  50, 130, 31, 69, 147, 58, 222, 178,
]);

export function getPreviewSwapExactInDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR,
  );
}

export type PreviewSwapExactInInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountLaunchFeeState extends string | AccountMeta<string> = string,
  TAccountBaseVault extends string | AccountMeta<string> = string,
  TAccountQuoteVault extends string | AccountMeta<string> = string,
  TAccountHookProgram extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountLaunch extends string
        ? ReadonlyAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountLaunchFeeState extends string
        ? ReadonlyAccount<TAccountLaunchFeeState>
        : TAccountLaunchFeeState,
      TAccountBaseVault extends string
        ? ReadonlyAccount<TAccountBaseVault>
        : TAccountBaseVault,
      TAccountQuoteVault extends string
        ? ReadonlyAccount<TAccountQuoteVault>
        : TAccountQuoteVault,
      TAccountHookProgram extends string
        ? ReadonlyAccount<TAccountHookProgram>
        : TAccountHookProgram,
      ...TRemainingAccounts,
    ]
  >;

export type PreviewSwapExactInInstructionData = {
  discriminator: ReadonlyUint8Array;
  amountIn: bigint;
  tradeDirection: number;
};

export type PreviewSwapExactInInstructionDataArgs = {
  amountIn: number | bigint;
  tradeDirection: number;
};

export function getPreviewSwapExactInInstructionDataEncoder(): FixedSizeEncoder<PreviewSwapExactInInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['amountIn', getU64Encoder()],
      ['tradeDirection', getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR,
    }),
  );
}

export function getPreviewSwapExactInInstructionDataDecoder(): FixedSizeDecoder<PreviewSwapExactInInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['amountIn', getU64Decoder()],
    ['tradeDirection', getU8Decoder()],
  ]);
}

export function getPreviewSwapExactInInstructionDataCodec(): FixedSizeCodec<
  PreviewSwapExactInInstructionDataArgs,
  PreviewSwapExactInInstructionData
> {
  return combineCodec(
    getPreviewSwapExactInInstructionDataEncoder(),
    getPreviewSwapExactInInstructionDataDecoder(),
  );
}

export type PreviewSwapExactInAsyncInput<
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountHookProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchFeeState?: Address<TAccountLaunchFeeState>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  /** Optional hook program (must match launch.hook_program if set) */
  hookProgram?: Address<TAccountHookProgram>;
  amountIn: PreviewSwapExactInInstructionDataArgs['amountIn'];
  tradeDirection: PreviewSwapExactInInstructionDataArgs['tradeDirection'];
};

export async function getPreviewSwapExactInInstructionAsync<
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountHookProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: PreviewSwapExactInAsyncInput<
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountHookProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  PreviewSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountHookProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: false },
    quoteVault: { value: input.quoteVault ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.launchFeeState.value) {
    accounts.launchFeeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            108, 97, 117, 110, 99, 104, 95, 102, 101, 101, 95, 115, 116, 97,
            116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('hookProgram', accounts.hookProgram),
    ],
    data: getPreviewSwapExactInInstructionDataEncoder().encode(
      args as PreviewSwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as PreviewSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountHookProgram
  >);
}

export type PreviewSwapExactInInput<
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountHookProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchFeeState: Address<TAccountLaunchFeeState>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  /** Optional hook program (must match launch.hook_program if set) */
  hookProgram?: Address<TAccountHookProgram>;
  amountIn: PreviewSwapExactInInstructionDataArgs['amountIn'];
  tradeDirection: PreviewSwapExactInInstructionDataArgs['tradeDirection'];
};

export function getPreviewSwapExactInInstruction<
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountHookProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: PreviewSwapExactInInput<
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountHookProgram
  >,
  config?: { programAddress?: TProgramAddress },
): PreviewSwapExactInInstruction<
  TProgramAddress,
  TAccountLaunch,
  TAccountLaunchFeeState,
  TAccountBaseVault,
  TAccountQuoteVault,
  TAccountHookProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: false },
    quoteVault: { value: input.quoteVault ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('hookProgram', accounts.hookProgram),
    ],
    data: getPreviewSwapExactInInstructionDataEncoder().encode(
      args as PreviewSwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as PreviewSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountHookProgram
  >);
}

export type ParsedPreviewSwapExactInInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    launch: TAccountMetas[0];
    launchFeeState: TAccountMetas[1];
    baseVault: TAccountMetas[2];
    quoteVault: TAccountMetas[3];
    /** Optional hook program (must match launch.hook_program if set) */
    hookProgram?: TAccountMetas[4] | undefined;
  };
  data: PreviewSwapExactInInstructionData;
};

export function parsePreviewSwapExactInInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedPreviewSwapExactInInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 5,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  const getNextOptionalAccount = () => {
    const accountMeta = getNextAccount();
    return accountMeta.address === INITIALIZER_PROGRAM_ADDRESS
      ? undefined
      : accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      launch: getNextAccount(),
      launchFeeState: getNextAccount(),
      baseVault: getNextAccount(),
      quoteVault: getNextAccount(),
      hookProgram: getNextOptionalAccount(),
    },
    data: getPreviewSwapExactInInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
